import { useMutation, useQueryClient } from '@tanstack/react-query';
import { leavePolicyService } from '@/services/leave/leavePolicyService';
import { leaveQueryKeys } from './queryKeys';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { devLog } from '@/utils/logger';
import type { 
  CreateLeavePolicyInput, 
  UpdateLeavePolicyInput,
  AssignPolicyToEmployeeInput,
  UpdateLeaveBalanceInput
} from '@/services/leave/leavePolicyTypes';

export const useLeavePolicyMutations = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const createLeavePolicy = useMutation({
    mutationFn: (input: CreateLeavePolicyInput) => leavePolicyService.createLeavePolicy(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.policies() });
      toast({
        title: "Success",
        description: "Leave policy created successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create leave policy",
        variant: "destructive",
      });
    },
  });

  const updateLeavePolicy = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateLeavePolicyInput }) => {
      devLog('[useLeavePolicyMutations] Mutation called for policy:', id);
      
      return leavePolicyService.updateLeavePolicy(id, input);
    },
    onMutate: ({ id }) => {
      devLog('[useLeavePolicyMutations] Mutation starting (onMutate) for:', id);
    },
    onSuccess: async (result, { id }) => {
      devLog('[useLeavePolicyMutations] Mutation success for:', id);
      
      try {
        // Invalidate and refetch all related queries with specific cache busting
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: leaveQueryKeys.policies() }),
          queryClient.invalidateQueries({ queryKey: leaveQueryKeys.policy(id) }),
          queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances() }),
          queryClient.invalidateQueries({ queryKey: leaveQueryKeys.policyAssignments(id) })
        ]);
        
        // Force fresh data by removing specific queries from cache and refetching
        queryClient.removeQueries({ queryKey: leaveQueryKeys.policy(id) });
        await Promise.all([
          queryClient.refetchQueries({ queryKey: leaveQueryKeys.policies() }),
          queryClient.prefetchQuery({ 
            queryKey: leaveQueryKeys.policy(id),
            staleTime: 0 
          })
        ]);
        
        devLog('[useLeavePolicyMutations] Cache invalidation and refetch completed');

        if (result.balancesRecalculated) {
          toast({
            title: "Policy Updated",
            description: result.employeesUpdated > 0 
              ? `Policy updated and ${result.employeesUpdated} employee balances recalculated`
              : "Your leave policy has been successfully updated",
          });
        } else {
          // Policy saved but balances failed — warn the admin
          toast({
            title: "Policy Updated — Balances Not Recalculated",
            description: "The policy was saved but employee balances could not be updated automatically. Please check balances manually or try editing the policy again.",
            variant: "default",
          });
          console.warn('[useLeavePolicyMutations] Balance recalc failed:', result.balanceError);
        }
      } catch (cacheError) {
        console.error('[useLeavePolicyMutations] Cache update failed:', cacheError);
        toast({
          title: "Update Successful", 
          description: "Policy updated successfully. Please refresh to see changes.",
        });
      }
    },
    onError: (error: Error, { id, input }) => {
      console.error('[useLeavePolicyMutations] Failed to update policy:', id, error);
      
      let errorMessage = "Failed to update leave policy";
      if (error.message.includes('not found')) {
        errorMessage = "Policy not found. It may have been deleted.";
      } else if (error.message.includes('permissions')) {
        errorMessage = "You don't have permission to update this policy";
      } else if (error.message.includes('name already exists')) {
        errorMessage = "A policy with this name already exists";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Update Failed", 
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const deleteLeavePolicy = useMutation({
    mutationFn: (id: string) => leavePolicyService.deleteLeavePolicy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.policies() });
      toast({
        title: "Policy Deactivated",
        description: "Policy has been deactivated successfully. It will no longer be available for new assignments, but existing data is preserved.",
      });
    },
    onError: (error: Error) => {
      let errorMessage = "Failed to deactivate policy";
      
      if (error.message.includes('active employee assignments')) {
        errorMessage = "Cannot deactivate policy with active employee assignments. Please reassign employees first.";
      } else if (error.message.includes('pending/approved leave request')) {
        errorMessage = "Cannot deactivate policy with pending or approved leave requests. Please resolve these first.";
      } else if (error.message.includes('dependencies')) {
        errorMessage = error.message; // Use the detailed dependency message
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Deactivation Failed",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const assignPolicyToEmployees = useMutation({
    mutationFn: (input: AssignPolicyToEmployeeInput) => 
      leavePolicyService.assignPolicyToEmployees(input),
    onSuccess: (_, input) => {
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.policies() });
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances() });
      // Specifically invalidate the policy assignments for this policy
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.policyAssignments(input.policy_id) });
      // Invalidate policy dependencies for real-time updates
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.policyDependencies(input.policy_id) });
      // Invalidate all policy assignment queries
      queryClient.invalidateQueries({ queryKey: [...leaveQueryKeys.all, 'policies'], type: 'all' });
      // Invalidate custom assignmentsWithProfiles queries
      queryClient.invalidateQueries({ queryKey: ['assignmentsWithProfiles'], type: 'all' });
      toast({
        title: "Success",
        description: "Policy assigned to employees successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to assign policy to employees",
        variant: "destructive",
      });
    },
  });

  const updateLeaveBalance = useMutation({
    mutationFn: (input: UpdateLeaveBalanceInput) => 
      leavePolicyService.updateLeaveBalance(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances() });
      toast({
        title: "Success",
        description: "Leave balance updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update leave balance",
        variant: "destructive",
      });
    },
  });

  const calculateInitialBalance = useMutation({
    mutationFn: async ({ policyId, profileId }: { policyId: string; profileId: string }) => {
      // Fetch policy to check accrual method
      const { data: policy } = await supabase
        .from('leave_policies')
        .select('accrual_method, name')
        .eq('id', policyId)
        .single();
      
      if (policy?.accrual_method === 'fixed_accrual') {
        throw new Error(
          `Cannot manually calculate initial balance for fixed accrual policy "${policy.name}". ` +
          `Balance is managed automatically by the scheduled release system.`
        );
      }
      
      return leavePolicyService.calculateInitialBalance(policyId, profileId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances() });
      toast({
        title: "Success",
        description: "Initial balance calculated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to calculate initial balance",
        variant: "destructive",
      });
    },
  });

  const reactivateLeavePolicy = useMutation({
    mutationFn: (policyId: string) => leavePolicyService.reactivateLeavePolicy(policyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.policies() });
      toast({
        title: "Policy Reactivated",
        description: "The leave policy has been successfully reactivated and is now available for assignment.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Reactivation Failed",
        description: error.message || "Failed to reactivate the policy. Please try again.",
        variant: "destructive",
      });
    },
  });

  const permanentlyDeleteLeavePolicy = useMutation({
    mutationFn: (policyId: string) => leavePolicyService.permanentlyDeleteLeavePolicy(policyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.policies() });
      toast({
        title: "Policy Deleted",
        description: "The leave policy has been permanently deleted along with all associated data.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Deletion Failed",
        description: error.message || "Failed to delete the policy. Please try again.",
        variant: "destructive",
      });
    },
  });

  const removePolicyAssignment = useMutation({
    mutationFn: (assignmentId: string) => leavePolicyService.removePolicyAssignment(assignmentId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.policies() });
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances() });
      // Specifically invalidate the policy assignments for the affected policy
      if (result?.policy_id) {
        queryClient.invalidateQueries({ queryKey: leaveQueryKeys.policyAssignments(result.policy_id) });
        // Invalidate policy dependencies for real-time updates
        queryClient.invalidateQueries({ queryKey: leaveQueryKeys.policyDependencies(result.policy_id) });
      }
      // Invalidate all policy assignment queries
      queryClient.invalidateQueries({ queryKey: [...leaveQueryKeys.all, 'policies'], type: 'all' });
      // Invalidate custom assignmentsWithProfiles queries
      queryClient.invalidateQueries({ queryKey: ['assignmentsWithProfiles'], type: 'all' });
      toast({
        title: "Success",
        description: "Employee assignment removed successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to remove assignment",
        variant: "destructive",
      });
    },
  });

  const removePolicyAssignments = useMutation({
    mutationFn: (assignmentIds: string[]) => leavePolicyService.removePolicyAssignments(assignmentIds),
    onSuccess: (results, assignmentIds) => {
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.policies() });
      queryClient.invalidateQueries({ queryKey: leaveQueryKeys.balances() });
      // Specifically invalidate policy assignments for affected policies
      if (results && Array.isArray(results)) {
        const policyIds = [...new Set(results.map(r => r.policy_id).filter(Boolean))];
        policyIds.forEach(policyId => {
          queryClient.invalidateQueries({ queryKey: leaveQueryKeys.policyAssignments(policyId) });
          // Invalidate policy dependencies for real-time updates
          queryClient.invalidateQueries({ queryKey: leaveQueryKeys.policyDependencies(policyId) });
        });
      }
      // Invalidate all policy assignment queries
      queryClient.invalidateQueries({ queryKey: [...leaveQueryKeys.all, 'policies'], type: 'all' });
      // Invalidate custom assignmentsWithProfiles queries
      queryClient.invalidateQueries({ queryKey: ['assignmentsWithProfiles'], type: 'all' });
      toast({
        title: "Success",
        description: `${assignmentIds.length} assignment(s) removed successfully`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to remove assignments",
        variant: "destructive",
      });
    },
  });

  return {
    createLeavePolicy,
    updateLeavePolicy,
    deleteLeavePolicy,
    reactivateLeavePolicy,
    permanentlyDeleteLeavePolicy,
    assignPolicyToEmployees,
    removePolicyAssignment,
    removePolicyAssignments,
    updateLeaveBalance,
    calculateInitialBalance,
  };
};